import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';

const useExcelUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const validateHeaders = (headers, requiredHeaders) => {
    const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
    const normalizedRequired = requiredHeaders.map(h => h.toLowerCase().trim());
    
    return normalizedRequired.every(required => 
      normalizedHeaders.some(header => header.includes(required))
    );
  };

  const processExcelFile = useCallback(async (file, requiredHeaders = ['insurance company name']) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            let workbook;
            
            // Handle CSV files differently
            if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
              const text = e.target.result;
              workbook = XLSX.read(text, { type: 'string' });
            } else {
              const data = new Uint8Array(e.target.result);
              workbook = XLSX.read(data, { type: 'array' });
            }
            
            // Get first worksheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length === 0) {
              throw new Error('Excel file is empty');
            }

            // Get headers (first row)
            const headers = jsonData[0] || [];
            
            // More flexible header validation - look for any column that might contain insurance names
            const possibleColumns = headers.map((header, index) => ({
              index,
              header: header?.toString().toLowerCase() || '',
              isInsuranceColumn: header?.toString().toLowerCase().includes('insurance') ||
                                header?.toString().toLowerCase().includes('company') ||
                                header?.toString().toLowerCase().includes('name')
            }));
            
            let insuranceColumnIndex = possibleColumns.find(col => 
              col.isInsuranceColumn && 
              (col.header.includes('insurance') || col.header.includes('company'))
            )?.index;

            // If no specific insurance column found, try the first column
            if (insuranceColumnIndex === undefined && headers.length > 0) {
              insuranceColumnIndex = 0;
            }

            if (insuranceColumnIndex === undefined) {
              throw new Error('No suitable column found for insurance company names');
            }

            // Get data rows (skip header)
            const dataRows = jsonData.slice(1);
            
            // Extract insurance company names
            const insuranceCompanies = dataRows
              .map(row => row[insuranceColumnIndex])
              .filter(name => name && typeof name === 'string' && name.trim())
              .map(name => name.trim());

            // Remove duplicates
            const uniqueInsuranceCompanies = [...new Set(insuranceCompanies)];

            setIsUploading(false);
            resolve({
              success: true,
              data: uniqueInsuranceCompanies,
              totalRows: dataRows.length,
              validRows: uniqueInsuranceCompanies.length
            });
          } catch (error) {
            setIsUploading(false);
            setUploadError(error.message);
            reject(error);
          }
        };

        reader.onerror = () => {
          setIsUploading(false);
          const error = new Error('Failed to read file');
          setUploadError(error.message);
          reject(error);
        };

        // Use appropriate reader method based on file type
        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
          reader.readAsText(file);
        } else {
          reader.readAsArrayBuffer(file);
        }
      });
    } catch (error) {
      setIsUploading(false);
      setUploadError(error.message);
      throw error;
    }
  }, []);

  const validateFile = useCallback((file) => {
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/csv'
    ];
    
    const allowedExtensions = ['.xls', '.xlsx', '.csv'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      throw new Error('Please upload only Excel (.xls, .xlsx) or CSV files');
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size must be less than 10MB');
    }

    return true;
  }, []);

  return {
    processExcelFile,
    validateFile,
    isUploading,
    uploadError,
    setUploadError
  };
};

export default useExcelUpload; 